import type { TOC } from '@ember/component/template-only';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import {
  PrismButton,
  PrismCard,
  PrismRadioGroup,
} from '@mylighthouse/prism-ember';
import { ICONS_LOGOS_MAP } from '@mylighthouse/prism-foundation';
import { t } from 'ember-intl';
import { eq, or } from 'ember-truth-helpers';
import { FocusScreen } from 'frontend-utils';
import GeneralIconWithTooltip from 'lumen/components/general/icon-with-tooltip';
import HotelBenchmarkInsightCompetitorsCompsetCardProposedCompset from 'lumen/components/hotel/benchmark-insight/competitors/compset/card-proposed-compset';
import HotelBenchmarkInsightCompetitorsCompsetBuilderCompsetBuilderHeader from 'lumen/components/hotel/benchmark-insight/competitors/compset-builder/compset-builder-header';
import HotelBenchmarkInsightCompetitorsCompsetBuilderStepperPanel from 'lumen/components/hotel/benchmark-insight/competitors/compset-builder/stepper-panel';
import HotelBenchmarkInsightCompetitorsTableCompetitors from 'lumen/components/hotel/benchmark-insight/competitors/table-competitors';
import type HotelSettingsCompetitorsBenchmarkCompsetBuilderProposalsController from 'lumen/controllers/hotel/settings/competitors/benchmark/compset/builder/proposals';

interface HotelSettingsCompetitorsBenchmarkCompsetBuilderProposalsSignature {
  Args: {
    controller: HotelSettingsCompetitorsBenchmarkCompsetBuilderProposalsController;
    model: unknown;
  };
}

<template>
  <FocusScreen
    @hasCancel={{true}}
    @isCancelDisabled={{false}}
    @logoHtml={{ICONS_LOGOS_MAP.logo-dark-with-text}}
    @onClose={{@controller.onClose}}
    @size="large"
  >
    <:header>
      <HotelBenchmarkInsightCompetitorsCompsetBuilderCompsetBuilderHeader
        @currentStep={{2}}
      />
    </:header>
    <:body>
      <div class="compset-builder__body">
        <div
          class="compset-builder__body__header bg-elevation-surface px-1000 pt-1000 pb-600"
        >
          <h1>{{t
              "settings.competitors.benchmark.compset.builder.proposals.title"
            }}</h1>
        </div>

        <div class="compset-builder__body__right-sidebar pr-1000 pl-500">
          <HotelBenchmarkInsightCompetitorsCompsetBuilderStepperPanel
            @description={{@controller.stepperPanelDescription}}
            @steps={{@controller.stepperPanelSteps}}
            @title={{t
              "settings.competitors.benchmark.compset.builder.stepper.proposals.title"
              count=@controller.visibleProposedCompsets.length
            }}
          />
        </div>

        <div class="compset-builder__body__content pr-500 pb-1000 pl-1000">
          <div class="flex flex-col gap-400">
            {{#if @controller.model.compset.last.value.competitors.length}}
              <PrismCard
                @contentLayout="noPadding"
                @isSecondary={{true}}
                class="prism-card--with-table-competitors prism-card--with-border"
              >
                <:header as |Header|>
                  <Header.title>
                    {{t
                      "settings.competitors.benchmark.compset.current_compset"
                    }}
                  </Header.title>
                </:header>

                <:content>
                  <HotelBenchmarkInsightCompetitorsTableCompetitors
                    @competitors={{@controller.model.compset.last.value.competitors}}
                    @isLoading={{@controller.model.compset.isRunning}}
                    @isReadOnly={{true}}
                    @isRowHoverDisabled={{true}}
                  />
                </:content>
              </PrismCard>

              <div class="divider-line" />
            {{/if}}

            <PrismRadioGroup
              @name="radio-group"
              @onChange={{@controller.onChangeProposedCompset}}
              @value={{@controller.radioGroupValue}}
              as |RadioGroup|
            >
              {{#each
                @controller.visibleProposedCompsets
                as |proposedCompset index|
              }}
                <HotelBenchmarkInsightCompetitorsCompsetCardProposedCompset
                  @index={{index}}
                  @isSelected={{eq
                    @controller.benchmarkInsightCompetitorsCompset.selectedProposedCompsetPrioritizedCategory
                    proposedCompset.prioritizedCategory
                  }}
                  @onClickCard={{fn
                    @controller.selectProposedCompset
                    proposedCompset.prioritizedCategory
                  }}
                  @proposedCompset={{proposedCompset}}
                >
                  <RadioGroup.input
                    @value={{proposedCompset.prioritizedCategory}}
                  />
                </HotelBenchmarkInsightCompetitorsCompsetCardProposedCompset>
              {{/each}}
            </PrismRadioGroup>

            {{#if @controller.unusedDesiredCompetitors.length}}
              <div class="divider-line" />

              <PrismCard
                @contentLayout="noPadding"
                @isSecondary={{true}}
                class="prism-card--with-table-competitors prism-card--with-border"
              >
                <:header as |Header|>
                  <div
                    class="text-200--regular flex flex-row items-center gap-100"
                  >
                    <Header.title>
                      {{t
                        "settings.competitors.benchmark.compset.builder.not_used"
                      }}
                    </Header.title>

                    <GeneralIconWithTooltip
                      @size="small"
                      @tooltipContent={{t
                        "settings.competitors.benchmark.compset.builder.not_used.tooltip"
                      }}
                    />
                  </div>
                </:header>

                <:content>
                  <HotelBenchmarkInsightCompetitorsTableCompetitors
                    {{! @glint-ignore: Class inheritance }}
                    @competitors={{@controller.unusedDesiredCompetitors}}
                    @isLoading={{or
                      @controller.model.proposedCompsets.isRunning
                      @controller.model.desiredCompset.isRunning
                    }}
                    @isReadOnly={{true}}
                    @isRowHoverDisabled={{true}}
                    @showDesiredCompetitorInfoColumn={{true}}
                  />
                </:content>
              </PrismCard>
            {{/if}}
          </div>
        </div>
      </div>
    </:body>
    <:footer>
      <div class="flex w-full flex-row justify-between gap-300">
        <PrismButton
          @buttonType="ghost"
          @iconBefore="arrow-left"
          @label={{t "general.back"}}
          {{on "click" @controller.onClickBack}}
        />

        <PrismButton
          @buttonType="brand"
          @iconAfter="arrow-right"
          @isDisabled={{@controller.isNextDisabled}}
          {{on "click" @controller.onClickNext}}
          @label={{t "general.next"}}
          data-test-id="compset-builder-proposals-button-next"
        />
      </div>

    </:footer>
  </FocusScreen>
</template> satisfies TOC<HotelSettingsCompetitorsBenchmarkCompsetBuilderProposalsSignature>;
